import { siteOrigin } from '../../native';
import { ApiError, HangoutInvite, InviteType, JoinBlockedReason, PublicHangoutInvite } from './types';

export const MAX_INVITE_MESSAGE = 200;
export const MAX_CHAT_MESSAGE = 1000;

export const INVITE_TYPES: { value: InviteType; title: string; description: string }[] = [
  { value: 'JustMyFriends', title: 'Just my friends', description: 'Share with people you know' },
  { value: 'FriendsOfFriends', title: 'Friends of friends', description: 'Your friends can invite their friends' },
  { value: 'PrivateGroup', title: 'Private group', description: 'Only approved people can join' }
];

export const inviteTypeLabel = (t: InviteType) => INVITE_TYPES.find((x) => x.value === t)?.title ?? t;

// ---------- Dates (shown in the visitor's local time; sent to the API with the offset) ----------

const pad = (n: number) => String(n).padStart(2, '0');

export const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const toTimeInput = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// "2026-12-27" + "21:30" → "2026-12-27T21:30:00+05:30" in the browser's timezone.
export function toOffsetIso(date: string, time: string): string {
  const local = new Date(`${date}T${time}:00`);
  const offset = -local.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  return `${date}T${time}:00${sign}${pad(Math.floor(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`;
}

export const formatInviteDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
export const formatInviteDay = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
export const formatInviteTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const formatWeekdayNight = (iso: string) => `${new Date(iso).toLocaleDateString('en-GB', { weekday: 'long' })} Night`;
export const formatChatTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const isPast = (iso: string) => new Date(iso).getTime() < Date.now();

// ---------- Create-invite validation (stands in for a Zod schema; same shape: field → message) ----------

export interface CreateInviteValues {
  venueId: number | null;
  date: string;
  time: string;
  inviteType: InviteType | '';
  message: string;
}

export type CreateInviteErrors = Partial<Record<keyof CreateInviteValues, string>>;

export function validateCreateInvite(v: CreateInviteValues, now = new Date()): CreateInviteErrors {
  const errors: CreateInviteErrors = {};
  if (!v.venueId) errors.venueId = 'Choose a venue.';
  if (!v.date) errors.date = 'Pick a date.';
  if (!v.time) errors.time = 'Pick a time.';
  if (v.date && v.time && new Date(`${v.date}T${v.time}:00`).getTime() <= now.getTime() + 5 * 60 * 1000)
    errors.time = 'Pick a time in the future.';
  if (!v.inviteType) errors.inviteType = 'Choose who can join.';
  if (v.message.length > MAX_INVITE_MESSAGE) errors.message = `Keep your message under ${MAX_INVITE_MESSAGE} characters.`;
  return errors;
}

// ---------- Links and share text ----------

// Public URL for a token. Participants in Friends-of-Friends invites add their ref code so the
// referral is recorded. Built from the site's origin (the public website when inside the app).
export function inviteUrl(token: string, refCode?: string | null): string {
  const url = `${siteOrigin()}/invite/${token}`;
  return refCode ? `${url}?ref=${encodeURIComponent(refCode)}` : url;
}

export function shareText(invite: Pick<HangoutInvite, 'venue' | 'inviteDateTime'>, url: string): string {
  return [
    "🎉 You're invited!",
    '',
    `Night out at ${invite.venue.name}`,
    '',
    `📅 ${formatInviteDay(invite.inviteDateTime)}`,
    `🕘 ${formatInviteTime(invite.inviteDateTime)}`,
    `📍 ${[invite.venue.area, invite.venue.city].filter(Boolean).join(', ')}`,
    '',
    'Join here:',
    url
  ].join('\n');
}

// Opens WhatsApp's composer with the message pre-filled; the user still picks the chat and taps send.
export const whatsAppUrl = (text: string) => `https://wa.me/?text=${encodeURIComponent(text)}`;
export const facebookUrl = (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

// ---------- Sign-in round trip ----------

const RETURN_KEY = 'c2n_return_url';
const PENDING_KEY = 'c2n_pending_hangout_action';

export type PendingAction = { token: string; action: 'Interested' | 'Going' | 'Requested' };

// Remember where to come back to (and what the visitor was trying to do), then go to login.
export function loginUrlFor(returnUrl: string, pending?: PendingAction): string {
  try {
    sessionStorage.setItem(RETURN_KEY, returnUrl);
    if (pending) sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    /* private mode: the ?returnUrl query still works */
  }
  return `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
}

// Only same-site paths are honoured, so a crafted link can't bounce people to another site.
export function safeReturnUrl(candidate: string | null): string | null {
  const url = candidate ?? (() => { try { return sessionStorage.getItem(RETURN_KEY); } catch { return null; } })();
  return url && url.startsWith('/') && !url.startsWith('//') ? url : null;
}

export function clearReturnUrl() {
  try { sessionStorage.removeItem(RETURN_KEY); } catch { /* ignore */ }
}

export function takePendingAction(token: string): PendingAction['action'] | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingAction;
    if (pending.token !== token) return null;
    sessionStorage.removeItem(PENDING_KEY);
    return pending.action;
  } catch {
    return null;
  }
}

// ---------- Error states ----------

export function errorTitle(error: ApiError | null, blocked?: JoinBlockedReason): { title: string; body: string } {
  const code = blocked ?? error?.code;
  switch (code) {
    case 'invalid_token':
    case 'not_found':
      return { title: 'Invite not found', body: "This invite link isn't valid any more. Ask your friend to share it again." };
    case 'expired':
      return { title: 'This night out has passed', body: 'The hangout already happened, so it no longer takes new people.' };
    case 'cancelled':
      return { title: 'Invite cancelled', body: 'The host cancelled this hangout.' };
    case 'removed':
      return { title: "You're no longer part of this hangout", body: 'The host removed you from this invite.' };
    case 'blocked':
      return { title: 'Invite unavailable', body: "This invite isn't available to you." };
    case 'referral_limit':
      return { title: 'Ask the host for their link', body: 'This invite has been passed on too many times. Ask the host to share it with you directly.' };
    case 'forbidden':
      return { title: 'Members only', body: error?.message ?? "You don't have access to this." };
    case 'network':
      return { title: "You're offline", body: 'Check your connection and try again.' };
    default:
      return { title: 'Something went wrong', body: error?.message ?? 'Please try again.' };
  }
}

export const canRespond = (inv: PublicHangoutInvite) => inv.status === 'Active' && !inv.joinBlockedReason && !inv.isHost;
